//#region Imports Packages and Libs

import { STORE_MUTATION } from "../../enums";
import { FeedbackTextPayload, IMessageFeedback, MessageFeedbackPayload, StateOption } from "../../types";
import { Util } from "../../util";

//#endregion

//#region Message Feedback Mutations

/**
 * Feedback mutations for handling message feedback actions.
 */
export const feedbackMutations = {
  // Message rating
  [STORE_MUTATION.SetMessageRating]: (state: StateOption, payload: MessageFeedbackPayload) => {
    try {
      // Skip if the payload, messageId or rating is not valid
      if (!payload || !payload?.messageId || typeof payload?.rating !== "number") return;
      const { messageId, rating } = payload;

      // Ensure feedbackCooldowns object exists
      state.feedbackCooldowns = state.feedbackCooldowns ?? Object.create(null);

      // Find the message in the state with the given messageId
      const message = state.messages.find((m) => m.id === messageId);
      const isInCooldown = Util.isFeedbackInCooldown(state.feedbackCooldowns, messageId);

      // Check if the message is valid and not in cooldown
      if (message && !isInCooldown) {
        // Initialize feedback object if it doesn't exist
        message.feedback = message.feedback || {};
        message.feedback.rating = rating;
        message.feedback.isRatingPending = true;

        // If an existing cooldown exists, clear its timeout to avoid leaks
        const existing = state.feedbackCooldowns[messageId];
        if (existing?.timeoutId) {
          clearTimeout(existing.timeoutId);
        }

        // Normalize duration (fallback to 5s if invalid)
        const durationInSecs = state.feedbackClickCooldownSeconds || 0;
        const duration = typeof durationInSecs === "number" && durationInSecs > 0 ? durationInSecs * 1000 : 5000;

        // Set cooldown
        const expiry = Date.now() + duration;
        const timeoutId = setTimeout(() => {
          try {
            // Safe cleanup of the cooldown entry after expiry
            delete state.feedbackCooldowns[messageId];
          } catch {}
        }, duration);

        state.feedbackCooldowns[messageId] = { expiry, timeoutId };
      }
    } catch (ex) {
      console.error("[mutations.ts] An error has occurred \n-> SetMessageFeedback -> Error: ", ex);
    }
  },
  [STORE_MUTATION.ConfirmMessageRating]: (state: StateOption, messageId: string) => {
    try {
      // Skip if messageId is not valid
      if (!messageId) return;

      // Find the message in the state with the given messageId
      const message = state.messages.find((m) => m.id === messageId);
      if (message?.feedback) {
        message.feedback.isRatingPending = false;
      }
    } catch (ex) {
      console.error("[mutations.ts] An error has occurred \n-> ConfirmMessageRating -> Error: ", ex);
    }
  },
  [STORE_MUTATION.RollbackMessageRating]: (state: StateOption, payload: MessageFeedbackPayload) => {
    try {
      // Skip if the payload or messageId is not valid
      if (!payload || !payload.messageId) return;

      // Find the message in the state with the given messageId
      const message = state.messages.find((m) => m.id === payload.messageId);
      if (!message) return;

      // Get previous feedback state
      const previousFeedback: IMessageFeedback = { rating: payload.rating };
      const hadFeedbackObject = !!message?.feedback;

      // Message never had feedback - set a default feedback object
      if (!hadFeedbackObject) {
        message.feedback = { isRatingPending: false };
      } else if (previousFeedback) {
        // Restore the exact previous feedback state
        const previousFeedbackState = { ...previousFeedback, isRatingPending: false };
        message.feedback = { ...previousFeedbackState };
      } else {
        // Had feedback object but it was empty/null
        message.feedback = { isRatingPending: false };
      }
    } catch (ex) {
      console.error("[mutations.ts] An error has occurred \n-> RollbackMessageRating -> Error: ", ex);
      try {
        const message = state.messages?.find((m) => m.id === payload?.messageId);
        // Force clear pending state even if rollback fails
        if (message?.feedback) message.feedback.isRatingPending = false;
      } catch {}
    }
  },

  // Message feedback cooldowns
  [STORE_MUTATION.ClearMessageFeedbackCooldown]: (state: StateOption, messageId: string) => {
    try {
      // Ensure feedbackCooldowns object exists
      state.feedbackCooldowns = state.feedbackCooldowns ?? Object.create(null);

      // Check if feedbackCooldowns is an object and has the messageId
      if (messageId && state.feedbackCooldowns && messageId in state.feedbackCooldowns) {
        // Clear the timeout before removing the entry
        const cooldownEntry = state.feedbackCooldowns[messageId];
        if (cooldownEntry?.timeoutId) {
          clearTimeout(cooldownEntry.timeoutId);
        }

        // Remove the messageId from feedbackCooldowns
        delete state.feedbackCooldowns[messageId];
      }
    } catch (ex) {
      console.error("[mutations.ts] An error has occurred \n-> ClearMessageFeedbackCooldown -> Error: ", ex);
    }
  },

  // Message feedbacks
  [STORE_MUTATION.SetMessageFeedback]: (state: StateOption, payload: FeedbackTextPayload) => {
    try {
      if (payload?.messageId && !Util.isNullOrEmpty(payload?.text)) {
        // Get the current entry for this messageId, or start with an empty object
        const prevEntry = state.feedbackTexts[payload.messageId] || {};

        // Create the new entry, updating text and submittedAt
        const newEntry = { ...prevEntry, text: payload.text, submittedAt: Date.now() };

        // Create a new feedbackTexts object with the updated entry
        state.feedbackTexts = { ...state.feedbackTexts, [payload.messageId]: newEntry };
      }
    } catch (ex) {
      console.error("[mutations.ts] An error has occurred \n-> ClearMessageFeedbackCooldown -> Error: ", ex);
    }
  },
  [STORE_MUTATION.ClearMessageFeedback]: (state: StateOption, messageId: string) => {
    try {
      // Ensure feedbackTexts object exists
      state.feedbackTexts = state.feedbackTexts ?? Object.create(null);

      // Check if feedbackTexts is an object and has the messageId
      if (messageId && state.feedbackTexts && messageId in state.feedbackTexts) {
        // Remove the messageId from feedbackTexts
        delete state.feedbackTexts[messageId];
      }
    } catch (ex) {
      console.error("[mutations.ts] An error has occurred \n-> ClearMessageFeedbackText -> Error: ", ex);
    }
  },

  // Message feedback configs
  [STORE_MUTATION.SetFeedbackDownButtonLabels]: (state: StateOption, value: string[]) => {
    state.feedbackDownButtonLabels = value;
  },
  [STORE_MUTATION.SetFeedbackClickCooldownSeconds]: (state: StateOption, value: number) => {
    state.feedbackClickCooldownSeconds = value;
  },
};

//#endregion
